<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AiCloserAPI from 'dashboard/api/aiCloser';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  advanceRollbackSessionContext,
  buildRollbackBreadcrumbBadges,
  normalizeRollbackSessionContext,
} from 'dashboard/routes/dashboard/ailab/assistantV2/fin/lib/releaseLoopSnapshots';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
  releaseEvidence: {
    type: Object,
    default: () => ({}),
  },
});
const emit = defineEmits(['updated']);
const route = useRoute();
const router = useRouter();

const dialogRef = ref(null);
const confirmRef = ref(null);

const isLoading = ref(false);
const versions = ref([]);
const publishedVersionId = ref(null);
const draftVersionId = ref(null);
const inboxes = ref([]);
const rollouts = ref({});

const selectedVersion = ref(null);
const rollingBack = ref(false);
const publishing = ref(false);
const publishConfirmRef = ref(null);
const publishInboxesRef = ref(null);
const publishScope = ref('all'); // 'all'|'inboxes'
const selectedInboxIds = ref([]);
const emptyDialogContext = () => ({
  intentLabel: '',
  bundleLabel: '',
  targetVersionId: null,
  targetVersionLabel: '',
  compareVersionId: null,
  compareVersionLabel: '',
  selectedVersionId: null,
  selectedVersionRole: '',
  sessionId: '',
  sessionLabel: '',
  sessionStep: 1,
  sessionOrigin: '',
  sessionItemKey: '',
});
const TEST_SEED_QUERY_KEYS = ['test_query', 'test_inbox_id', 'test_reason', 'test_source'];
const TEST_COMPARE_QUERY_KEYS = [
  'test_compare_bundle',
  'test_compare_title',
  'test_compare_focus_label',
  'test_compare_strategy_group_title',
  'test_compare_recommendation_code',
  'test_compare_owner_workspace',
  'test_compare_owner_action_title',
  'test_compare_guidance_editor',
  'test_compare_escalation_focus',
  'test_compare_hotspot_summary',
  'test_compare_resolved_summary',
  'test_compare_escalated_summary',
  'test_compare_validation_summary',
  'test_compare_stage',
];
const TEST_FOLLOWUP_QUERY_KEYS = [
  'test_followup_intent',
  'test_followup_target_version_label',
  'test_followup_compare_version_label',
  'test_followup_session_id',
  'test_followup_session_label',
  'test_followup_session_step',
  'test_followup_session_origin',
  'test_followup_session_item_key',
];
const ANALYZE_SEED_QUERY_KEYS = [
  'analyze_seed',
  'analyze_source',
  'analyze_time_window',
  'analyze_inbox_id',
  'analyze_content_gap_reason',
  'analyze_article_scope_warning',
  'analyze_delivery_mode',
];
const ANALYZE_FOLLOWUP_QUERY_KEYS = [
  'analyze_followup_intent',
  'analyze_followup_target_version_label',
  'analyze_followup_compare_version_label',
  'analyze_followup_session_id',
  'analyze_followup_session_label',
  'analyze_followup_session_step',
  'analyze_followup_session_origin',
  'analyze_followup_session_item_key',
];
const DEPLOY_SEED_QUERY_KEYS = [
  'deploy_seed',
  'deploy_seed_source',
  'deploy_seed_focus',
  'deploy_seed_reason',
  'deploy_seed_inbox_id',
  'deploy_seed_confidence_level',
  'deploy_seed_confidence_runs',
  'deploy_seed_confidence_title',
  'deploy_seed_batch_baseline_key',
  'deploy_seed_batch_set_key',
  'deploy_seed_batch_title',
  'deploy_seed_batch_overall_level',
  'deploy_seed_batch_total_cases',
  'deploy_seed_batch_ready_cases',
  'deploy_seed_batch_watch_cases',
  'deploy_seed_batch_blocked_cases',
  'deploy_seed_batch_ran_at',
  'deploy_seed_batch_top_blocker',
  'deploy_seed_batch_top_watch_signal',
  'deploy_seed_followup_intent',
  'deploy_seed_target_version_label',
  'deploy_seed_compare_version_label',
  'deploy_seed_prefill_note',
  'deploy_seed_open_versions',
  'deploy_seed_session_id',
  'deploy_seed_session_label',
  'deploy_seed_session_step',
  'deploy_seed_session_origin',
  'deploy_seed_session_item_key',
];
const DEPLOY_COMPARE_QUERY_KEYS = [
  'deploy_seed_bundle',
  'deploy_seed_title',
  'deploy_seed_focus_label',
  'deploy_seed_strategy_group_title',
  'deploy_seed_recommendation_code',
  'deploy_seed_owner_workspace',
  'deploy_seed_owner_action_title',
  'deploy_seed_guidance_editor',
  'deploy_seed_escalation_focus',
  'deploy_seed_hotspot_summary',
  'deploy_seed_resolved_summary',
  'deploy_seed_escalated_summary',
  'deploy_seed_validation_summary',
  'deploy_seed_stage',
  'deploy_seed_rollback_trigger',
];
const dialogContext = ref(emptyDialogContext());

const title = computed(() => 'AI Closer config versions');
const contextSummary = computed(() => {
  const parts = [];
  if (dialogContext.value.intentLabel) parts.push(dialogContext.value.intentLabel);
  if (dialogContext.value.bundleLabel) parts.push(dialogContext.value.bundleLabel);
  if (dialogContext.value.targetVersionLabel) {
    parts.push(`target ${dialogContext.value.targetVersionLabel}`);
  }
  if (dialogContext.value.compareVersionLabel) {
    parts.push(`compare ${dialogContext.value.compareVersionLabel}`);
  }
  return parts.join(' • ');
});
const normalizeDialogContext = context => {
  const targetVersionId = Number(context?.target_version_id || context?.targetVersionId || 0);
  const compareVersionId = Number(
    context?.compare_version_id || context?.previous_version_id || context?.compareVersionId || 0
  );
  const selectedVersionId = Number(
    context?.selected_version_id || context?.selectedVersionId || targetVersionId || 0
  );
  const session = normalizeRollbackSessionContext({
    sessionId: context?.session_id || context?.sessionId,
    sessionLabel: context?.session_label || context?.sessionLabel,
    sessionStep: context?.session_step || context?.sessionStep,
    sessionOrigin: context?.session_origin || context?.sessionOrigin,
  });
  return {
    intentLabel: String(
      context?.intent_label || context?.label || context?.intentLabel || ''
    ).trim(),
    bundleLabel: String(context?.bundle_label || context?.bundleLabel || '').trim(),
    targetVersionId: targetVersionId > 0 ? targetVersionId : null,
    targetVersionLabel: String(
      context?.target_version_label || context?.targetVersionLabel || ''
    ).trim(),
    compareVersionId: compareVersionId > 0 ? compareVersionId : null,
    compareVersionLabel: String(
      context?.compare_version_label ||
        context?.previous_version_label ||
        context?.compareVersionLabel ||
        ''
    ).trim(),
    selectedVersionId: selectedVersionId > 0 ? selectedVersionId : null,
    selectedVersionRole: String(
      context?.selected_version_role || context?.selectedVersionRole || ''
    ).trim(),
    sessionId: session.sessionId,
    sessionLabel: session.sessionLabel,
    sessionStep: session.sessionStep,
    sessionOrigin: session.sessionOrigin,
    sessionItemKey: String(context?.session_item_key || context?.sessionItemKey || '').trim(),
  };
};
const rowContextLabel = v => {
  const versionId = Number(v?.id || 0);
  if (versionId > 0 && versionId === Number(dialogContext.value.targetVersionId || 0)) {
    return 'rollback target';
  }
  if (versionId > 0 && versionId === Number(dialogContext.value.compareVersionId || 0)) {
    return 'rolled-off baseline';
  }
  return '';
};
const selectedVersionContextNote = computed(() => {
  if (!selectedVersion.value?.id) return '';
  const note = rowContextLabel(selectedVersion.value);
  return note ? `Selected ${note}.` : '';
});
const contextBadges = computed(() => {
  const badges = buildRollbackBreadcrumbBadges({
    source: 'Deploy',
    intentLabel: dialogContext.value.intentLabel,
    targetVersionLabel: dialogContext.value.targetVersionLabel,
    compareVersionLabel: dialogContext.value.compareVersionLabel,
    sessionLabel: dialogContext.value.sessionLabel,
    sessionStep: dialogContext.value.sessionStep,
    sessionOrigin: dialogContext.value.sessionOrigin,
  });
  if (dialogContext.value.selectedVersionRole) {
    badges.push({
      label: `selected ${dialogContext.value.selectedVersionRole}`,
      tone: 'muted',
    });
  }
  return badges;
});
const buildRollbackCompareBundle = () => ({
  enabled: Boolean(
    props.releaseEvidence?.bundle_title ||
      props.releaseEvidence?.focus_label ||
      props.releaseEvidence?.recommendation_code
  ),
  title: String(props.releaseEvidence?.bundle_title || dialogContext.value.bundleLabel || '').trim(),
  focusLabel: String(props.releaseEvidence?.focus_label || '').trim(),
  strategyGroupTitle: '',
  recommendationCode: String(props.releaseEvidence?.recommendation_code || '').trim(),
  ownerWorkspace: 'deploy',
  ownerActionTitle: String(dialogContext.value.intentLabel || 'Rollback operator flow').trim(),
  guidanceEditor: '',
  escalationFocus: '',
  hotspotSummary: '',
  resolvedSummary: '',
  escalatedSummary: '',
  validationSummary:
    dialogContext.value.targetVersionLabel && dialogContext.value.compareVersionLabel
      ? `${dialogContext.value.targetVersionLabel} vs ${dialogContext.value.compareVersionLabel}`
      : '',
  stage: 'validation',
});
const activeReleaseEvidence = computed(() => {
  const evidence =
    props.releaseEvidence && typeof props.releaseEvidence === 'object'
      ? { ...props.releaseEvidence }
      : {};
  const session = normalizeRollbackSessionContext({
    sessionId: dialogContext.value.sessionId || evidence.session_id || evidence.sessionId,
    sessionLabel:
      dialogContext.value.sessionLabel || evidence.session_label || evidence.sessionLabel,
    sessionStep:
      dialogContext.value.sessionStep || evidence.session_step || evidence.sessionStep,
    sessionOrigin:
      dialogContext.value.sessionOrigin ||
      evidence.session_origin ||
      evidence.sessionOrigin,
    nextWorkspace: 'Versions',
  });
  if (session.sessionId) {
    evidence.session_id = session.sessionId;
    evidence.session_label = session.sessionLabel;
    evidence.session_step = session.sessionStep;
    evidence.session_origin = session.sessionOrigin || 'Deploy';
    evidence.next_workspace = session.nextWorkspace || 'Versions';
  }
  return evidence;
});
const buildNextDialogSessionContext = nextWorkspace =>
  advanceRollbackSessionContext({
    current: {
      sessionId: dialogContext.value.sessionId,
      sessionLabel: dialogContext.value.sessionLabel,
      sessionStep: dialogContext.value.sessionStep,
      sessionOrigin: dialogContext.value.sessionOrigin,
    },
    sourceWorkspace: 'Versions',
    nextWorkspace,
  });
const openTestWorkspaceForRollback = async () => {
  const nextQuery = { ...(route.query || {}), tab: 'test_preview' };
  DEPLOY_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_COMPARE_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  ANALYZE_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  ANALYZE_FOLLOWUP_QUERY_KEYS.forEach(key => delete nextQuery[key]);

  nextQuery.test_source = 'Rollback operator';
  nextQuery.test_reason = dialogContext.value.intentLabel || 'Rollback follow-up';
  nextQuery.test_query =
    dialogContext.value.bundleLabel || dialogContext.value.targetVersionLabel || '';

  const compareBundle = buildRollbackCompareBundle();
  if (compareBundle.enabled) {
    nextQuery.test_compare_bundle = '1';
    if (compareBundle.title) nextQuery.test_compare_title = compareBundle.title;
    if (compareBundle.focusLabel) nextQuery.test_compare_focus_label = compareBundle.focusLabel;
    if (compareBundle.recommendationCode) {
      nextQuery.test_compare_recommendation_code = compareBundle.recommendationCode;
    }
    if (compareBundle.ownerWorkspace) {
      nextQuery.test_compare_owner_workspace = compareBundle.ownerWorkspace;
    }
    if (compareBundle.ownerActionTitle) {
      nextQuery.test_compare_owner_action_title = compareBundle.ownerActionTitle;
    }
    if (compareBundle.validationSummary) {
      nextQuery.test_compare_validation_summary = compareBundle.validationSummary;
    }
    if (compareBundle.stage) nextQuery.test_compare_stage = compareBundle.stage;
  }
  if (dialogContext.value.intentLabel) {
    nextQuery.test_followup_intent = dialogContext.value.intentLabel;
  }
  if (dialogContext.value.targetVersionLabel) {
    nextQuery.test_followup_target_version_label = dialogContext.value.targetVersionLabel;
  }
  if (dialogContext.value.compareVersionLabel) {
    nextQuery.test_followup_compare_version_label = dialogContext.value.compareVersionLabel;
  }
  const session = buildNextDialogSessionContext('Test');
  nextQuery.test_followup_session_id = session.sessionId;
  nextQuery.test_followup_session_label = session.sessionLabel;
  nextQuery.test_followup_session_step = String(session.sessionStep || 1);
  nextQuery.test_followup_session_origin = session.sessionOrigin || 'Deploy';
  if (session.sessionId) {
    nextQuery.rollback_session_key = session.sessionId;
  }
  if (dialogContext.value.sessionItemKey) {
    nextQuery.test_followup_session_item_key = dialogContext.value.sessionItemKey;
    nextQuery.rollback_session_item_key = dialogContext.value.sessionItemKey;
  }
  await router.replace({ query: nextQuery });
};
const openAnalyzeWorkspaceForRollback = async () => {
  const nextQuery = { ...(route.query || {}), tab: 'analyze_decision_log', analyze_seed: '1' };
  DEPLOY_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  DEPLOY_COMPARE_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_COMPARE_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  TEST_FOLLOWUP_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  ANALYZE_SEED_QUERY_KEYS.forEach(key => delete nextQuery[key]);
  ANALYZE_FOLLOWUP_QUERY_KEYS.forEach(key => delete nextQuery[key]);

  nextQuery.analyze_source = 'Rollback operator';
  nextQuery.analyze_time_window = '7d';
  if (dialogContext.value.intentLabel) {
    nextQuery.analyze_followup_intent = dialogContext.value.intentLabel;
  }
  if (dialogContext.value.targetVersionLabel) {
    nextQuery.analyze_followup_target_version_label = dialogContext.value.targetVersionLabel;
  }
  if (dialogContext.value.compareVersionLabel) {
    nextQuery.analyze_followup_compare_version_label = dialogContext.value.compareVersionLabel;
  }
  const session = buildNextDialogSessionContext('Analyze');
  nextQuery.analyze_followup_session_id = session.sessionId;
  nextQuery.analyze_followup_session_label = session.sessionLabel;
  nextQuery.analyze_followup_session_step = String(session.sessionStep || 1);
  nextQuery.analyze_followup_session_origin = session.sessionOrigin || 'Deploy';
  if (session.sessionId) {
    nextQuery.rollback_session_key = session.sessionId;
  }
  if (dialogContext.value.sessionItemKey) {
    nextQuery.analyze_followup_session_item_key = dialogContext.value.sessionItemKey;
    nextQuery.rollback_session_item_key = dialogContext.value.sessionItemKey;
  }
  await router.replace({ query: nextQuery });
};
const resetDialogState = () => {
  selectedVersion.value = null;
  dialogContext.value = emptyDialogContext();
};

const load = async () => {
  isLoading.value = true;
  try {
    const resp = await AiCloserAPI.getConfigVersions(props.assistantId);
    versions.value = resp?.data?.versions || [];
    publishedVersionId.value = resp?.data?.published_version_id || null;
    draftVersionId.value = resp?.data?.draft_version_id || null;
    inboxes.value = resp?.data?.inboxes || [];
    rollouts.value = resp?.data?.rollouts || {};
  } catch (e) {
    useAlert(
      e?.response?.data?.error || e?.message || 'Failed to load versions'
    );
  } finally {
    isLoading.value = false;
  }
};

const open = async context => {
  dialogContext.value = normalizeDialogContext(context);
  await load();
  const preferredId = Number(dialogContext.value.selectedVersionId || 0);
  selectedVersion.value =
    versions.value.find(v => Number(v?.id || 0) === preferredId) ||
    versions.value.find(v => Number(v?.id || 0) === Number(dialogContext.value.targetVersionId || 0)) ||
    null;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const askRollback = v => {
  selectedVersion.value = v;
  confirmRef.value?.open();
};

const doRollback = async () => {
  const v = selectedVersion.value;
  if (!v?.id) return;
  rollingBack.value = true;
  try {
    await AiCloserAPI.rollbackConfig(props.assistantId, v.id, activeReleaseEvidence.value);
    useAlert(`Rolled back to version #${v.version_number}`);
    // Reload list after rollback to show the new snapshot.
    await load();
    emit('updated');
  } catch (e) {
    useAlert(e?.response?.data?.error || e?.message || 'Failed to rollback');
  } finally {
    rollingBack.value = false;
    confirmRef.value?.close();
  }
};

const statusLabel = v => {
  if (!v?.id) return '-';
  if (Number(v.id) === Number(publishedVersionId.value)) return 'published';
  if (Number(v.id) === Number(draftVersionId.value)) return 'draft';
  return v?.state || 'snapshot';
};

const canPublish = v => Number(v?.id) !== Number(publishedVersionId.value);
const selectContextVersion = role => {
  const targetId =
    role === 'compare'
      ? Number(dialogContext.value.compareVersionId || 0)
      : Number(dialogContext.value.targetVersionId || 0);
  if (!targetId) return;
  selectedVersion.value =
    versions.value.find(v => Number(v?.id || 0) === targetId) || selectedVersion.value;
};

const askPublishAll = v => {
  selectedVersion.value = v;
  publishScope.value = 'all';
  publishConfirmRef.value?.open();
};

const openPublishInboxes = v => {
  selectedVersion.value = v;
  publishScope.value = 'inboxes';
  // default select all inboxes currently attached to this assistant
  selectedInboxIds.value = (inboxes.value || []).map(i => i.id);
  publishInboxesRef.value?.open();
};

const doPublish = async () => {
  const v = selectedVersion.value;
  if (!v?.id) return;
  publishing.value = true;
  try {
    if (publishScope.value === 'inboxes') {
      await AiCloserAPI.publishConfig(props.assistantId, {
        versionId: v.id,
        scope: 'inboxes',
        inboxIds: selectedInboxIds.value || [],
        releaseEvidence: activeReleaseEvidence.value,
      });
      useAlert(`Published version #${v.version_number} to selected inboxes`);
      publishInboxesRef.value?.close();
    } else {
      await AiCloserAPI.publishConfig(props.assistantId, {
        versionId: v.id,
        scope: 'all',
        inboxIds: [],
        releaseEvidence: activeReleaseEvidence.value,
      });
      useAlert(`Published version #${v.version_number} to all inboxes`);
      publishConfirmRef.value?.close();
    }
    await load();
    emit('updated');
  } catch (e) {
    useAlert(e?.response?.data?.error || e?.message || 'Failed to publish');
  } finally {
    publishing.value = false;
  }
};

const setReview = async v => {
  if (!v?.id) return;
  try {
    await AiCloserAPI.setConfigVersionState(props.assistantId, {
      versionId: v.id,
      state: 'review',
    });
    useAlert(`Marked version #${v.version_number} as review`);
    await load();
    emit('updated');
  } catch (e) {
    useAlert(
      e?.response?.data?.error || e?.message || 'Failed to update state'
    );
  }
};

defineExpose({ open, close, reload: load });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    description="View recent AI Closer config snapshots and rollback."
    width="3xl"
    :show-confirm-button="false"
    :is-loading="isLoading"
    @close="resetDialogState"
  >
    <div class="flex items-center justify-between gap-3">
      <div class="text-sm text-n-slate-11">
        Showing latest {{ versions.length }} versions (max 50).
      </div>
      <Button
        icon="i-lucide-refresh-cw"
        color="slate"
        variant="outline"
        size="sm"
        label="Refresh"
        :is-loading="isLoading"
        :disabled="isLoading"
        @click="load"
      />
    </div>
    <div v-if="contextSummary" class="mt-3 text-sm text-n-slate-11">
      Context: {{ contextSummary }}
    </div>
    <div
      v-if="contextBadges.length"
      class="mt-3 flex flex-wrap gap-2"
    >
      <span
        v-for="badge in contextBadges"
        :key="badge.label"
        class="inline-flex items-center rounded-full bg-n-slate-3 px-2 py-1 text-xs font-medium text-n-slate-11"
        :class="{ 'bg-n-teal-2 text-n-teal-11': badge.tone === 'primary' }"
      >
        {{ badge.label }}
      </span>
    </div>
    <div
      v-if="contextSummary || selectedVersion"
      class="mt-3 rounded-md border border-n-alpha-2 bg-n-slate-2 p-3"
    >
      <div class="text-sm text-n-slate-12">
        {{ selectedVersionContextNote || 'Use this rollback context to inspect or act on the target versions.' }}
      </div>
      <div class="mt-2 flex flex-wrap gap-2">
        <Button
          v-if="dialogContext.targetVersionId"
          color="slate"
          variant="outline"
          size="sm"
          label="Focus rollback target"
          @click="selectContextVersion('target')"
        />
        <Button
          v-if="dialogContext.compareVersionId"
          color="slate"
          variant="outline"
          size="sm"
          label="Focus rolled-off baseline"
          @click="selectContextVersion('compare')"
        />
        <Button
          v-if="selectedVersion?.id && selectedVersion.state !== 'review'"
          color="slate"
          variant="outline"
          size="sm"
          label="Mark selected review"
          @click="setReview(selectedVersion)"
        />
        <Button
          v-if="selectedVersion?.id && canPublish(selectedVersion)"
          color="teal"
          variant="outline"
          size="sm"
          label="Publish selected"
          @click="askPublishAll(selectedVersion)"
        />
        <Button
          v-if="selectedVersion?.id && canPublish(selectedVersion) && inboxes.length"
          color="teal"
          variant="outline"
          size="sm"
          label="Grey release selected"
          @click="openPublishInboxes(selectedVersion)"
        />
        <Button
          v-if="selectedVersion?.id"
          color="ruby"
          variant="outline"
          size="sm"
          label="Rollback to selected"
          @click="askRollback(selectedVersion)"
        />
        <Button
          v-if="dialogContext.targetVersionLabel || dialogContext.bundleLabel"
          color="slate"
          variant="outline"
          size="sm"
          label="Open Test compare"
          @click="openTestWorkspaceForRollback"
        />
        <Button
          v-if="dialogContext.targetVersionLabel || dialogContext.bundleLabel"
          color="slate"
          variant="outline"
          size="sm"
          label="Open Analyze traces"
          @click="openAnalyzeWorkspaceForRollback"
        />
      </div>
    </div>

    <div v-if="!versions.length" class="mt-4 text-sm text-n-slate-11">
      No versions yet. Save the config to create snapshots.
    </div>

    <div v-else class="mt-4 overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-11">
            <th class="py-2 pr-4">Version</th>
            <th class="py-2 pr-4">Status</th>
            <th class="py-2 pr-4">Reason</th>
            <th class="py-2 pr-4">Created at</th>
            <th class="py-2 pr-4">User</th>
            <th class="py-2 pr-0 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="v in versions"
            :key="v.id"
            class="border-t border-n-alpha-2 cursor-pointer"
            :class="{
              'bg-n-slate-2': Number(selectedVersion?.id || 0) === Number(v.id || 0),
              'bg-n-teal-2': Number(dialogContext.targetVersionId || 0) === Number(v.id || 0),
              'bg-n-amber-2': Number(dialogContext.compareVersionId || 0) === Number(v.id || 0),
            }"
            @click="selectedVersion = v"
          >
            <td class="py-2 pr-4 text-n-slate-12">
              #{{ v.version_number }}
              <div v-if="rowContextLabel(v)" class="text-xs text-n-slate-11">
                {{ rowContextLabel(v) }}
              </div>
            </td>
            <td class="py-2 pr-4 text-n-slate-11">{{ statusLabel(v) }}</td>
            <td class="py-2 pr-4 text-n-slate-11">{{ v.reason || '-' }}</td>
            <td class="py-2 pr-4 text-n-slate-11">{{ v.created_at || '-' }}</td>
            <td class="py-2 pr-4 text-n-slate-11">
              {{ v.created_by_user_id || '-' }}
            </td>
            <td class="py-2 pr-0 text-right">
              <Button
                v-if="v.state !== 'review'"
                class="mr-2"
                color="slate"
                variant="outline"
                size="sm"
                label="Mark review"
                @click="setReview(v)"
              />
              <Button
                v-if="canPublish(v)"
                class="mr-2"
                color="teal"
                variant="outline"
                size="sm"
                label="Publish all"
                @click="askPublishAll(v)"
              />
              <Button
                v-if="canPublish(v) && inboxes.length"
                class="mr-2"
                color="teal"
                variant="outline"
                size="sm"
                label="Publish to inboxes"
                @click="openPublishInboxes(v)"
              />
              <Button
                color="ruby"
                variant="outline"
                size="sm"
                label="Rollback"
                @click="askRollback(v)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Dialog>

  <Dialog
    ref="publishConfirmRef"
    type="alert"
    :title="`Publish version #${selectedVersion?.version_number || ''}`"
    description="This will make the selected snapshot the live config for all inboxes (and clear any per-inbox rollouts)."
    cancel-button-label="Cancel"
    confirm-button-label="Publish"
    :disable-confirm-button="publishing"
    :is-loading="publishing"
    @confirm="doPublish"
  />

  <Dialog
    ref="publishInboxesRef"
    :title="`Publish version #${selectedVersion?.version_number || ''} to inboxes`"
    description="Grey release: only selected inboxes will use this version. Others keep the globally published version."
    width="2xl"
    confirm-button-label="Publish"
    cancel-button-label="Cancel"
    :disable-confirm-button="publishing || !selectedInboxIds.length"
    :is-loading="publishing"
    @confirm="doPublish"
  >
    <div v-if="!inboxes.length" class="text-sm text-n-slate-11">
      No inboxes attached to this assistant.
    </div>
    <div v-else class="mt-2 space-y-2">
      <div class="text-sm text-n-slate-11">
        Select inboxes ({{ selectedInboxIds.length }}/{{ inboxes.length }}).
      </div>
      <div
        class="max-h-64 overflow-y-auto border border-n-alpha-2 rounded-md p-3"
      >
        <label
          v-for="i in inboxes"
          :key="i.id"
          class="flex items-center justify-between gap-3 py-1 text-sm"
        >
          <div class="text-n-slate-12">
            {{ i.name || `Inbox #${i.id}` }}
            <span class="ml-2 text-n-slate-11">{{ i.channel_type || '' }}</span>
          </div>
          <input v-model="selectedInboxIds" type="checkbox" :value="i.id" />
        </label>
      </div>
    </div>
  </Dialog>

  <Dialog
    ref="confirmRef"
    type="alert"
    :title="`Rollback to version #${selectedVersion?.version_number || ''}`"
    description="This will replace the current AI Closer config with the selected snapshot."
    cancel-button-label="Cancel"
    confirm-button-label="Rollback"
    :disable-confirm-button="rollingBack"
    :is-loading="rollingBack"
    @confirm="doRollback"
  />
</template>
